import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  Configuration,
  ConfigurationParameters,
  CitySageApi,
} from '@city-sage/core-client';
import { LoadingScreen } from '../../components/skeletons/LoadingScreen';
import { useAuth, useIsAuthenticated } from '../auth/AuthProvider';
import { useRouteAuth } from '../auth/use-route-auth';
import { useQuery } from '@tanstack/react-query';

interface RepositoryProps {
  children: React.ReactNode;
}

type ProviderRepositories = {
  api: CitySageApi;
};

const RepositoryContext = createContext<ProviderRepositories>(
  {} as ProviderRepositories
);

export const useRepository = () => useContext(RepositoryContext);

export function RepositoryProvider({ children }: RepositoryProps) {
  const { getToken, isInProgress, silentAuthAttempted } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const { requiresAuth } = useRouteAuth();
  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useQuery({
    queryKey: ['token'],
    queryFn: async () => {
      const token = await getToken();
      console.debug('[REPOSITORY] Token fetched', {
        success: !!token,
        timestamp: new Date().toISOString(),
      });
      setToken(token);
      return token;
    },
    enabled: isAuthenticated,
    refetchInterval: 1000 * 30,
    staleTime: 2 * 60 * 1000, // 2 minutes (refresh before expiry)
  });

  // Log initial state
  useEffect(() => {
    console.debug('[REPOSITORY] Provider initialized', {
      isAuthenticated,
      isInProgress,
      requiresAuth,
      hasToken: !!token,
      silentAuthAttempted,
      isReady,
      timestamp: new Date().toISOString(),
    });
  }, [
    isAuthenticated,
    isInProgress,
    requiresAuth,
    token,
    silentAuthAttempted,
    isReady,
  ]);

  // Initialize token and readiness
  useEffect(() => {
    const initialize = async () => {
      // Don't attempt to fetch token until silent auth has been attempted
      if (!silentAuthAttempted) {
        console.debug(
          '[REPOSITORY] Not fetching token (silent auth not attempted)'
        );
        return;
      }

      console.debug('[REPOSITORY] Fetching token', {
        isAuthenticated,
        requiresAuth,
        timestamp: new Date().toISOString(),
      });

      if (isAuthenticated) {
        const accessToken = await getToken();
        console.debug('[REPOSITORY] Token fetched', {
          success: !!accessToken,
          timestamp: new Date().toISOString(),
        });
        setToken(accessToken);
        // Only set ready if we have a token
        setIsReady(!!accessToken);
      } else {
        console.debug('[REPOSITORY] Clearing token (not authenticated)');
        setToken(null);
        setIsReady(true); // Set ready even without token for unauthenticated routes
      }
    };

    initialize();
  }, [isAuthenticated, getToken, silentAuthAttempted]);

  // Create the API client configuration
  const apiConfig = useMemo(() => {
    // Don't create config until we're ready
    if (!isReady) {
      console.debug('[REPOSITORY] Not creating API config (not ready)', {
        silentAuthAttempted,
        hasToken: !!token,
        timestamp: new Date().toISOString(),
      });
      return null;
    }

    console.debug('[REPOSITORY] Creating API config', {
      hasToken: !!token,
      timestamp: new Date().toISOString(),
    });

    // Determine the correct base path based on environment
    const isCapacitor = Capacitor.isNativePlatform();
    let basePath: string;

    if (isCapacitor) {
      // For Capacitor mobile apps, use environment variable or fallback to localhost
      // In production, VITE_API_BASE_URL should point to your production API
      // In development, it points to your local backend
      basePath =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

      console.debug('[REPOSITORY] Capacitor platform detected:', {
        platform: Capacitor.getPlatform(),
        basePath,
      });
    } else {
      // For web browser, use the current origin (works with Vite proxy)
      basePath = window.location.origin + '/api';
    }

    console.debug('[REPOSITORY] Using API base path:', basePath);

    const config: ConfigurationParameters = {
      basePath,
      fetchApi: async (url, options) => {
        console.debug('[REPOSITORY] API request', {
          url,
          method: options?.method,
          hasAuthHeader: !!(
            options?.headers &&
            'Authorization' in (options.headers as Record<string, string>)
          ),
          timestamp: new Date().toISOString(),
        });

        const response = await fetch(url, options);

        console.debug('[REPOSITORY] API response', {
          url,
          status: response.status,
          ok: response.ok,
          timestamp: new Date().toISOString(),
        });

        return response;
      },
    };

    // If we have a token, add it to the headers
    if (token) {
      config.headers = {
        Authorization: `Bearer ${token}`,
      };
      console.debug('[REPOSITORY] Added auth token to API config');
    }

    return config;
  }, [token, isReady]);

  // Create the repositories object
  const repositories = useMemo(() => {
    // Don't create repositories until we're ready
    if (!isReady) {
      console.debug('[REPOSITORY] Will not create API client (not ready)', {
        isInProgress,
        silentAuthAttempted,
        hasToken: !!token,
        timestamp: new Date().toISOString(),
      });
      return null;
    }

    // Always create the client once auth state is known, including logged-out
    // visits. Protected screens are gated by AuthGuard (sign-in prompt) so the
    // shell never unmounts into a blank page. Public routes such as
    // /itineraries call the API without a token.
    if (requiresAuth && !isAuthenticated) {
      console.debug(
        '[REPOSITORY] Creating unauthenticated API client (protected route shows sign-in prompt)'
      );
    }

    // For all other cases, create the API client
    console.debug('[REPOSITORY] Creating API client', {
      isAuthenticated,
      requiresAuth,
      hasToken: !!token,
      isInProgress,
      silentAuthAttempted,
      timestamp: new Date().toISOString(),
    });

    return {
      api: new CitySageApi(new Configuration(apiConfig!)),
    };
  }, [
    apiConfig,
    requiresAuth,
    isAuthenticated,
    token,
    isInProgress,
    silentAuthAttempted,
    isReady,
  ]);

  // Never leave a blank document while the client is still starting.
  if (!repositories) {
    console.debug(
      '[REPOSITORY] Showing loading screen (repositories not ready)',
      {
        isInProgress,
        requiresAuth,
        isAuthenticated,
        hasToken: !!token,
        silentAuthAttempted,
        isReady,
        timestamp: new Date().toISOString(),
      }
    );
    return <LoadingScreen />;
  }

  console.debug('[REPOSITORY] Rendering with repositories');
  return (
    <RepositoryContext.Provider value={repositories}>
      {children}
    </RepositoryContext.Provider>
  );
}
